/**
 * When viewing a nested campaign (drill into a mission's sub-campaign), tasks in that
 * sprint are evaluated only against their local unlock graph. If the parent mission
 * that owns this nested campaign is still locked, status changes on inner missions
 * should warn the same way as on the parent — using the parent's blocking dependencies.
 */

import { calculateLockStates } from './calculateLockStates';

/**
 * If current sprint is nested under a parent campaign, returns lock info for the parent
 * mission whose nestedSprintId points at this sprint, when that parent is locked.
 *
 * @param {Object|null} currentSprint - The sprint being viewed (e.g. localSprint)
 * @param {Array} allSprints - All sprint records (flat list)
 * @param {Array} statuses - Fallback status defs when parent has none
 * @returns {Object|null} { parentTask, parentTaskName, blockingTasks, lockReason }
 */
export function getParentMissionLockInfo(currentSprint, allSprints, statuses, now = Date.now()) {
  if (!currentSprint?.parentSprintId || !allSprints?.length) return null;

  const parentSprint = allSprints.find((s) => s.id === currentSprint.parentSprintId);
  if (!parentSprint?.tasks?.length) return null;

  const parentTask = parentSprint.tasks.find((t) => t.nestedSprintId === currentSprint.id);
  if (!parentTask) return null;

  const parentNestedSprints = allSprints.filter((s) => s.parentSprintId === parentSprint.id);
  const parentStatuses = parentSprint.statuses || statuses;

  const parentLockStates = calculateLockStates(
    parentSprint.tasks,
    parentNestedSprints,
    parentStatuses,
    now,
  );
  const ls = parentLockStates[parentTask.id];
  if (!ls?.isLocked) return null;

  return {
    parentTask,
    parentTaskName: parentTask.name || 'Parent mission',
    parentSprintId: parentSprint.id,
    parentCampaignName: parentSprint.name || 'Parent campaign',
    parentMissionId: parentTask.id,
    blockingTasks: ls.blockingTasks || [],
    lockReason: ls.lockReason,
  };
}

/**
 * For each nested task that is not already locked locally, apply the parent's lock.
 */
export function mergeParentMissionLockIntoLockStates(lockStates, nestedTaskIds, parentInfo) {
  if (!parentInfo) return lockStates;

  const out = { ...lockStates };
  for (const id of nestedTaskIds) {
    const prev = out[id];
    if (!prev) continue;
    if (prev.isLocked) continue;

    out[id] = {
      ...prev,
      isLocked: true,
      lockReason: `Parent mission "${parentInfo.parentTaskName}" is locked: ${parentInfo.lockReason || 'dependencies not met'}`,
      blockingTasks: parentInfo.blockingTasks,
      isLockedByParentMission: true,
      parentMissionName: parentInfo.parentTaskName,
      parentCampaignName: parentInfo.parentCampaignName,
      parentMissionId: parentInfo.parentMissionId,
      parentSprintId: parentInfo.parentSprintId,
    };
  }
  return out;
}

/**
 * Runs calculateLockStates, then applies parent-mission lock when viewing a nested sprint.
 *
 * @param {Object} [context] - { currentSprint, allSprints, now }
 */
export function calculateLockStatesWithParentContext(
  tasks,
  nestedSprints,
  statuses,
  context,
) {
  const now = context?.now ?? Date.now();
  const base = calculateLockStates(tasks, nestedSprints, statuses, now);
  if (!context?.currentSprint?.parentSprintId || !context?.allSprints?.length) {
    return base;
  }

  const parentInfo = getParentMissionLockInfo(context.currentSprint, context.allSprints, statuses, now);
  if (!parentInfo) return base;

  const nestedTaskIds = tasks.map((t) => t.id);
  return mergeParentMissionLockIntoLockStates(base, nestedTaskIds, parentInfo);
}
